import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Upload, 
  RotateCw, 
  Download, 
  Share2, 
  RefreshCw, 
  Sparkles, 
  Trash2, 
  Check, 
  Info, 
  ZoomIn, 
  ArrowRight,
  X,
  Smartphone
} from 'lucide-react';
import { useLogo } from '../contexts/LogoContext';
import { useCelebration } from '../contexts/CelebrationContext';
import toast from 'react-hot-toast';

// Available Decorative Festive Frames
const FRAMES = [
  {
    id: 'dhol-tasha',
    name: 'Dhol Tasha Rhythms',
    subtitle: 'Official Beats of Washington',
    badge: '🥁 Cultural Beats',
    primaryColor: '#ea580c',
    secondaryColor: '#f59e0b',
    borderColor: '#ea580c',
    description: 'Festive saffron flags, royal dhol drums, and official BOW branding.'
  },
  {
    id: 'ganesh-utsav',
    name: 'Ganesh Utsav 2026',
    subtitle: 'Washington State Celebration',
    badge: '🌺 Ganesh Festival',
    primaryColor: '#dc2626',
    secondaryColor: '#f97316',
    borderColor: '#e11d48',
    description: 'Traditional marigold floral toran, golden diyas, and festive temple arch.'
  },
  {
    id: 'diwali-navratri',
    name: 'Festival of Lights',
    subtitle: 'Diwali & Navratri Celebration',
    badge: '🪔 Deepotsav',
    primaryColor: '#7c3aed',
    secondaryColor: '#f59e0b',
    borderColor: '#9333ea',
    description: 'Gleaming golden lamps, rangoli patterns, and starlit celebrations.'
  },
  {
    id: 'volunteer-pride',
    name: 'Proud BOW Volunteer',
    subtitle: 'Community Impact & Leadership',
    badge: '🤝 Community Hero',
    primaryColor: '#059669',
    secondaryColor: '#10b981',
    borderColor: '#059669',
    description: 'Special honorary frame celebrating dedicated non-profit volunteers.'
  },
  {
    id: 'royal-polaroid',
    name: 'Royal Heritage Polaroid',
    subtitle: 'Preserving Cultural Roots',
    badge: '✨ Heritage',
    primaryColor: '#b45309',
    secondaryColor: '#d97706',
    borderColor: '#d97706',
    description: 'Elegant cream polaroid style with embossed gold foil details.'
  }
];

// Available Draggable Stickers
const STICKERS = [
  { id: 'dhol', emoji: '🥁', label: 'Dhol' },
  { id: 'tasha', emoji: '🪘', label: 'Tasha' },
  { id: 'flag', emoji: '🚩', label: 'Bhagwa Flag' },
  { id: 'diya', emoji: '🪔', label: 'Diya' },
  { id: 'garland', emoji: '🌺', label: 'Marigold' },
  { id: 'sparkles', emoji: '✨', label: 'Sparkles' },
  { id: 'fireworks', emoji: '🎆', label: 'Celebration' },
  { id: 'turban', emoji: '👑', label: 'Pheta / Turban' },
  { id: 'heart', emoji: '🧡', label: 'BOW Heart' },
  { id: 'namaste', emoji: '🙏', label: 'Namaste' }
];

const PhotoBoothPage = () => {
  const { logoUrl } = useLogo();
  const { triggerConfetti } = useCelebration();
  
  // Camera & Image State
  const [photo, setPhoto] = useState(null); // base64 or image object
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' or 'environment'
  const [countdown, setCountdown] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState(FRAMES[0].id);
  const [filterMode, setFilterMode] = useState('normal'); // 'normal', 'warm', 'vibrant', 'bw'
  
  // Transformation & Customization State
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [placedStickers, setPlacedStickers] = useState([]);
  const [activeStickerId, setActiveStickerId] = useState(null);
  const [customText, setCustomText] = useState('Washington Cultural Celebration');
  const [isExporting, setIsExporting] = useState(false);
  const [savedModalImage, setSavedModalImage] = useState(null); // { url, blob, fileName }

  // DOM Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const nativeCameraInputRef = useRef(null);
  const photoContainerRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // Attach active stream to video element whenever video element or camera state updates
  useEffect(() => {
    if (isCameraActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.onloadedmetadata = () => {
        setIsCameraReady(true);
        videoRef.current?.play().catch(e => console.log('Autoplay play error:', e));
      };
    }
  }, [isCameraActive]);

  // Initialize and clean up WebRTC camera
  const startCamera = async (facing = facingMode) => {
    stopCamera();
    setCameraError(null);
    setIsCameraReady(false);

    try {
      const constraints = {
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1280 },
          height: { ideal: 1280 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setIsCameraActive(true);

      // If video ref is already in DOM, attach immediately
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraReady(true);
          videoRef.current?.play().catch(e => console.log('Video play error:', e));
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraError('Live browser camera is restricted. You can tap "Take Photo (Phone Camera)" or upload any picture below!');
      setIsCameraActive(false);
      setIsCameraReady(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setIsCameraReady(false);
  };

  // Flip camera between front & rear
  const toggleCameraFacing = () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextMode);
    if (isCameraActive) {
      startCamera(nextMode);
    }
  };

  // Capture Photo with 3-second Countdown
  const captureWithCountdown = () => {
    if (countdown !== null) return;
    let count = 3;
    setCountdown(count);

    const interval = setInterval(() => {
      count -= 1;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);
        snapPhoto();
      }
    }, 1000);
  };

  // Snap the actual camera frame
  const snapPhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error('Camera feed is still loading. Please wait a moment and try again.');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const vWidth = video.videoWidth || 1280;
      const vHeight = video.videoHeight || 1280;
      canvas.width = vWidth;
      canvas.height = vHeight;
      const ctx = canvas.getContext('2d');

      // Mirror front selfie camera so text and poses look natural
      if (facingMode === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      setPhoto(dataUrl);
      stopCamera();
      
      try { triggerConfetti(); } catch (e) {}
      toast.success('Awesome photo captured! Select your frame and stickers below.');
    } catch (err) {
      console.error('Snap error:', err);
      toast.error('Could not capture frame. Please try native camera or upload.');
    }
  };

  // Handle Local Photo Upload or Native Device Camera Photo
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP).');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPhoto(event.target.result);
      stopCamera();
      setZoom(1);
      setPanX(0);
      setPanY(0);
      try { triggerConfetti(); } catch (e) {}
      toast.success('Photo loaded successfully!');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Retake / Clear photo
  const handleRetake = () => {
    setPhoto(null);
    setPlacedStickers([]);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    startCamera();
  };

  // Add a Sticker to Photo
  const addSticker = (sticker) => {
    const newSticker = {
      instanceId: Date.now() + Math.random(),
      emoji: sticker.emoji,
      x: 50 + (Math.random() * 20 - 10), // percentage coordinates
      y: 50 + (Math.random() * 20 - 10),
      size: 52,
      rotation: Math.floor(Math.random() * 20 - 10)
    };
    setPlacedStickers(prev => [...prev, newSticker]);
    setActiveStickerId(newSticker.instanceId);
    toast.success(`Added ${sticker.emoji} sticker!`);
  };

  // Remove active sticker
  const removeSticker = (instanceId) => {
    setPlacedStickers(prev => prev.filter(s => s.instanceId !== instanceId));
    if (activeStickerId === instanceId) {
      setActiveStickerId(null);
    }
  };

  // Apply robust cross-platform color filters
  const applyCanvasFilters = (ctx, width, height, mode) => {
    if (mode === 'normal') return;

    if (mode === 'warm') {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'rgba(255, 205, 130, 0.3)';
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'color-burn';
      ctx.fillStyle = 'rgba(255, 160, 0, 0.15)';
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    } else if (mode === 'vibrant') {
      ctx.save();
      ctx.globalCompositeOperation = 'soft-light';
      ctx.fillStyle = 'rgba(255, 80, 0, 0.25)';
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'overlay';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    } else if (mode === 'bw') {
      // 100% Guaranteed Vintage Grayscale across all devices (no CORS/security limits)
      ctx.save();
      // Step 1: Remove all color saturation to pure monochrome
      ctx.globalCompositeOperation = 'color';
      ctx.fillStyle = '#808080';
      ctx.fillRect(0, 0, width, height);

      // Step 2: Add warm vintage sepia tone and film contrast
      ctx.globalCompositeOperation = 'soft-light';
      ctx.fillStyle = 'rgba(215, 175, 130, 0.4)';
      ctx.fillRect(0, 0, width, height);

      ctx.globalCompositeOperation = 'overlay';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }
  };

  // Draw full composite directly onto target canvas
  const drawCompositeArtwork = useCallback(async (targetCanvas) => {
    if (!targetCanvas || !photo) return;

    const size = 1200; // Standard high resolution square
    targetCanvas.width = size;
    targetCanvas.height = size;
    const ctx = targetCanvas.getContext('2d');

    const currentFrame = FRAMES.find(f => f.id === selectedFrame) || FRAMES[0];

    // 1. Fill Dark Slate Background
    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, size, size);

    // 2. Load User Photo
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = photo;

    await new Promise((resolve) => {
      if (img.complete) resolve();
      else { img.onload = resolve; img.onerror = resolve; }
    });

    const imgAspect = (img.width || 1) / (img.height || 1);
    let drawW = size;
    let drawH = size;
    let offsetX = 0;
    let offsetY = 0;

    if (imgAspect > 1) {
      drawW = size * imgAspect;
      drawH = size;
      offsetX = -(drawW - size) / 2;
    } else {
      drawW = size;
      drawH = size / imgAspect;
      offsetY = -(drawH - size) / 2;
    }

    // Draw user photo with center-based Zoom & Pan
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(panX * (size / 300), panY * (size / 300));
    ctx.translate(-size / 2, -size / 2);

    // Filter sub-canvas
    const photoSubCanvas = document.createElement('canvas');
    photoSubCanvas.width = size;
    photoSubCanvas.height = size;
    const pCtx = photoSubCanvas.getContext('2d');

    if (filterMode === 'warm') {
      pCtx.filter = 'sepia(0.3) saturate(1.35) contrast(1.05)';
    } else if (filterMode === 'vibrant') {
      pCtx.filter = 'saturate(1.5) contrast(1.15) brightness(1.02)';
    } else if (filterMode === 'bw') {
      pCtx.filter = 'grayscale(1) contrast(1.2)';
    }

    pCtx.drawImage(img, offsetX, offsetY, drawW, drawH);
    applyCanvasFilters(pCtx, size, size, filterMode);

    ctx.drawImage(photoSubCanvas, 0, 0);
    ctx.restore();

    // 3. Draw Festive Frame Borders
    ctx.save();

    // Outer Decorative Border
    ctx.strokeStyle = currentFrame.borderColor;
    ctx.lineWidth = 22;
    ctx.strokeRect(11, 11, size - 22, size - 22);

    // Inner Gold Foil Line
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, size - 48, size - 48);

    // Corner festive flourishes
    const cornerSize = 90;
    const drawCorner = (x, y, flipX, flipY) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(flipX, flipY);
      ctx.fillStyle = currentFrame.primaryColor;
      ctx.beginPath();
      ctx.arc(0, 0, cornerSize, 0, Math.PI / 2);
      ctx.lineTo(0, 0);
      ctx.fill();
      ctx.fillStyle = '#fbbf24';
      ctx.font = '32px sans-serif';
      ctx.fillText('🥁', 20, 56);
      ctx.restore();
    };

    drawCorner(26, 26, 1, 1);
    drawCorner(size - 26, 26, -1, 1);
    drawCorner(26, size - 26, 1, -1);
    drawCorner(size - 26, size - 26, -1, -1);

    // Top Header Banner with Selected Frame Palette
    const bannerGrad = ctx.createLinearGradient(size * 0.1, 0, size * 0.9, 0);
    bannerGrad.addColorStop(0, currentFrame.borderColor);
    bannerGrad.addColorStop(0.5, currentFrame.primaryColor);
    bannerGrad.addColorStop(1, currentFrame.borderColor);

    ctx.fillStyle = bannerGrad;
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.roundRect(size * 0.1, 32, size * 0.8, 96, 20);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Top Gold Trim
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw BOW Logo inside header (with local fallback guarantee)
    const effectiveLogo = logoUrl || '/logo512.png';
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.src = effectiveLogo;

    await new Promise((r) => {
      if (logoImg.complete && logoImg.naturalWidth) {
        r();
      } else {
        logoImg.onload = r;
        logoImg.onerror = () => {
          // If remote logo fails CORS, fallback to local logo
          if (logoImg.src !== window.location.origin + '/logo512.png') {
            logoImg.src = '/logo512.png';
            logoImg.onload = r;
            logoImg.onerror = r;
          } else {
            r();
          }
        };
      }
    });

    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(size * 0.18, 80, 36, 0, Math.PI * 2);
    ctx.fill();
    if (logoImg.complete && logoImg.naturalWidth) {
      ctx.clip();
      ctx.drawImage(logoImg, size * 0.18 - 36, 80 - 36, 72, 72);
    } else {
      ctx.fillStyle = '#ea580c';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('BOW', size * 0.18, 80);
    }
    ctx.restore();

    // Header Typography
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px "Cinzel", "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText('BEATS OF WASHINGTON', size * 0.54, 72);

    ctx.fillStyle = '#fef08a';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText(currentFrame.subtitle.toUpperCase(), size * 0.54, 104);

    // Bottom Footer Banner with Selected Frame Palette
    const footGrad = ctx.createLinearGradient(size * 0.08, 0, size * 0.92, 0);
    footGrad.addColorStop(0, currentFrame.borderColor);
    footGrad.addColorStop(0.5, currentFrame.secondaryColor || currentFrame.primaryColor);
    footGrad.addColorStop(1, currentFrame.borderColor);

    ctx.fillStyle = footGrad;
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.roundRect(size * 0.08, size - 116, size * 0.84, 80, 18);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(customText, size * 0.5, size - 74);

    ctx.fillStyle = '#fde68a';
    ctx.font = '16px sans-serif';
    ctx.fillText('✦ 501(c)(3) Non-Profit • beatsofwa.org ✦', size * 0.5, size - 46);

    ctx.restore();

    // 4. Draw User Placed Stickers
    placedStickers.forEach(sticker => {
      ctx.save();
      const px = (sticker.x / 100) * size;
      const py = (sticker.y / 100) * size;
      ctx.translate(px, py);
      ctx.rotate((sticker.rotation * Math.PI) / 180);
      ctx.font = `${sticker.size * 1.8}px "Segoe UI Emoji", "Apple Color Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 10;
      ctx.fillText(sticker.emoji, 0, 0);
      ctx.restore();
    });
  }, [photo, selectedFrame, filterMode, zoom, panX, panY, placedStickers, customText, logoUrl]);

  // Continuously update live preview canvas whenever photo/frame/settings change
  useEffect(() => {
    if (photo && previewCanvasRef.current) {
      drawCompositeArtwork(previewCanvasRef.current);
    }
  }, [photo, selectedFrame, filterMode, zoom, panX, panY, placedStickers, customText, logoUrl, drawCompositeArtwork]);

  // Download High-Definition Photo directly from the live rendered canvas
  const handleDownload = async () => {
    if (!photo || !previewCanvasRef.current) return;
    setIsExporting(true);

    try {
      const canvas = previewCanvasRef.current;
      canvas.toBlob((blob) => {
        if (!blob) throw new Error('Blob creation failed');
        
        const blobUrl = URL.createObjectURL(blob);
        const fileName = `Beats-of-Washington-Festival-${Date.now()}.jpg`;

        // Direct programmatic anchor download
        const link = document.createElement('a');
        link.download = fileName;
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Store rendered image for modal / tap-and-hold saving on phones
        setSavedModalImage({
          url: blobUrl,
          blob: blob,
          fileName: fileName
        });

        try { triggerConfetti(); } catch (e) {}
        toast.success('Festival photo generated! Saved to your downloads.');
      }, 'image/jpeg', 0.95);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download photo. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Direct Social Share / Save to Gallery directly from live rendered canvas
  const handleShare = async () => {
    if (!photo || !previewCanvasRef.current) return;
    setIsExporting(true);

    try {
      const canvas = previewCanvasRef.current;
      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error('Blob generation failed');

        const fileName = `Beats-of-Washington-Festival-${Date.now()}.jpg`;
        const blobUrl = URL.createObjectURL(blob);
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        // Check if device supports sharing images directly (iOS/Android Share Sheet)
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Beats of Washington Festival Photo',
              text: 'Celebrating community, music, and culture with Beats of Washington! 🥁✨ https://beatsofwa.org #BeatsOfWashington'
            });
            try { triggerConfetti(); } catch (e) {}
            toast.success('Shared successfully!');
          } catch (shareErr) {
            if (shareErr.name !== 'AbortError') {
              setSavedModalImage({ url: blobUrl, blob, fileName });
            }
          }
        } else {
          setSavedModalImage({ url: blobUrl, blob, fileName });
          
          if (navigator.clipboard) {
            navigator.clipboard.writeText('https://beatsofwa.org/photobooth').catch(() => {});
          }
          toast.success('Festival photo ready! Tap & hold to save to Photos or share.');
        }
      }, 'image/jpeg', 0.95);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        toast.error('Could not process photo share.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const activeFrameData = FRAMES.find(f => f.id === selectedFrame) || FRAMES[0];

  return (
    <>
      <Helmet>
        <title>Festival Photo Booth - Beats of Washington</title>
        <meta 
          name="description" 
          content="Capture and customize your festival selfies with official Beats of Washington frames, Dhol-Tasha stickers, and instant WhatsApp & Instagram sharing." 
        />
      </Helmet>

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950 text-white py-8 sm:py-12 border-b border-orange-500/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500 rounded-full blur-3xl" />
        </div>

        <div className="container-custom px-4 sm:px-6 relative z-10 text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs font-bold uppercase tracking-wider mb-3">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            Interactive Digital Photo Booth
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-2 sm:mb-3">
            Capture Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-400 to-rose-400">Festival Moments</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-300 max-w-xl mx-auto">
            Snap a live festival selfie, pick an official branded frame, add festive Dhol stickers, and share with your family & friends in 1 click!
          </p>
        </div>
      </div>

      {/* Main Studio Area */}
      <div className="bg-gray-900 min-h-screen py-6 sm:py-10 text-white">
        <div className="container-custom px-4 sm:px-6 max-w-5xl mx-auto">
          
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 items-start">
            
            {/* Left / Center: Interactive Canvas & Camera Screen (7 cols) */}
            <div className="lg:col-span-7 space-y-4">
              
              {/* Photo Display Card */}
              <div 
                ref={photoContainerRef}
                className="relative aspect-square w-full max-w-md mx-auto bg-slate-950 rounded-3xl overflow-hidden shadow-2xl border-4 border-orange-500/40 flex items-center justify-center group"
              >
                {/* 1. Live Camera Stream (Always mounted for immediate WebRTC binding) */}
                <div className={`relative w-full h-full ${isCameraActive && !photo ? 'block' : 'hidden'}`}>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    webkit-playsinline="true"
                    className={`w-full h-full object-cover ${facingMode === 'user' ? '-scale-x-100' : ''}`}
                  />
                  
                  {/* Countdown Overlay */}
                  {countdown !== null && (
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-30">
                      <div className="text-7xl sm:text-8xl font-black text-yellow-400 animate-ping">
                        {countdown}
                      </div>
                    </div>
                  )}

                  {/* Camera Control overlay */}
                  <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3 sm:gap-4 z-20 px-4">
                    <button
                      type="button"
                      onClick={toggleCameraFacing}
                      className="p-3 rounded-full bg-black/70 hover:bg-black text-white backdrop-blur-md border border-white/20 transition-all hover:scale-110 shadow-lg"
                      title="Flip Camera (Front / Rear)"
                      aria-label="Flip Camera"
                    >
                      <RotateCw className="w-5 h-5" />
                    </button>

                    <button
                      type="button"
                      onClick={captureWithCountdown}
                      disabled={!isCameraReady}
                      className="p-4 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-rose-500 text-white font-bold shadow-xl shadow-orange-500/40 transform transition-all hover:scale-110 active:scale-95 flex items-center gap-2 disabled:opacity-50"
                    >
                      <Camera className="w-6 h-6" />
                      <span className="text-sm font-black pr-1">{isCameraReady ? 'Snap (3s)' : 'Starting...'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={stopCamera}
                      className="p-3 rounded-full bg-red-600/80 hover:bg-red-600 text-white backdrop-blur-md transition-all hover:scale-110 shadow-lg"
                      title="Close Camera"
                      aria-label="Close Camera"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* 2. Photo Active Preview (Direct Real-time Canvas Rendering) */}
                {photo && (
                  <div className="relative w-full h-full select-none overflow-hidden flex items-center justify-center">
                    <canvas 
                      ref={previewCanvasRef} 
                      width={1200} 
                      height={1200} 
                      className="w-full h-full object-contain select-none"
                    />

                    {/* Interactive Sticker Overlay for easy selection & deletion */}
                    {placedStickers.map(sticker => (
                      <div
                        key={sticker.instanceId}
                        onClick={() => setActiveStickerId(sticker.instanceId)}
                        className={`absolute cursor-pointer select-none transform -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-125 ${
                          activeStickerId === sticker.instanceId ? 'ring-2 ring-yellow-400 rounded-full p-1 bg-black/60 shadow-lg' : ''
                        }`}
                        style={{
                          left: `${sticker.x}%`,
                          top: `${sticker.y}%`,
                          fontSize: `${sticker.size * 0.75}px`,
                          transform: `translate(-50%, -50%) rotate(${sticker.rotation}deg)`
                        }}
                      >
                        <span className="drop-shadow-md">{sticker.emoji}</span>
                        {activeStickerId === sticker.instanceId && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSticker(sticker.instanceId);
                            }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-[10px] shadow-md font-bold"
                            title="Remove sticker"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* 3. Empty State (Start camera or upload) */}
                {!isCameraActive && !photo && (
                  <div className="text-center p-6 space-y-3.5 max-w-xs">
                    <div className="w-16 h-16 rounded-3xl bg-gradient-to-tr from-orange-500 to-amber-400 text-white flex items-center justify-center mx-auto shadow-xl shadow-orange-500/30 animate-bounce">
                      <Camera className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-white mb-1">Ready for Your Photo!</h3>
                      <p className="text-xs text-gray-400">
                        Take a live festival selfie or choose a photo from your gallery.
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 pt-1">
                      {/* Option A: Live In-Browser Viewfinder */}
                      <button
                        type="button"
                        onClick={() => startCamera('user')}
                        className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-xs sm:text-sm shadow-lg shadow-orange-500/25 transition-all flex items-center justify-center gap-2 hover:scale-105"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Open Live Viewfinder</span>
                      </button>

                      {/* Option B: Native Device Camera (100% Reliable on all Phones) */}
                      <button
                        type="button"
                        onClick={() => nativeCameraInputRef.current?.click()}
                        className="w-full py-2.5 px-4 rounded-xl bg-orange-950/80 hover:bg-orange-900 text-orange-200 font-bold text-xs sm:text-sm border border-orange-500/40 transition-all flex items-center justify-center gap-2 hover:scale-105"
                      >
                        <Sparkles className="w-4 h-4 text-yellow-400" />
                        <span>Snap with Phone Camera</span>
                      </button>

                      {/* Option C: File Picker */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full py-2.5 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs sm:text-sm border border-white/20 transition-all flex items-center justify-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload from Photos</span>
                      </button>
                    </div>

                    {cameraError && (
                      <p className="text-[11px] text-amber-300 leading-tight bg-amber-950/60 p-2 rounded-lg border border-amber-500/30">
                        {cameraError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Hidden File Inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <input
                ref={nativeCameraInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Primary Action Buttons (When Photo is Ready) */}
              {photo && (
                <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isExporting}
                    className="flex-1 min-w-[140px] py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-xs sm:text-sm shadow-lg shadow-orange-500/30 transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download HD Photo</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleShare}
                    disabled={isExporting}
                    className="flex-1 min-w-[140px] py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/30 transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share to WhatsApp / Insta</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleRetake}
                    className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white border border-white/20 transition-all hover:scale-105"
                    title="Retake or Choose Another Photo"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Right Column: Customization & Controls (5 cols) */}
            <div className="lg:col-span-5 space-y-6 bg-slate-950/80 p-5 sm:p-6 rounded-3xl border border-gray-800">
              
              {/* Step 1: Frame Selection */}
              <div>
                <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span>1. Choose Festive Frame</span>
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {FRAMES.map(frame => {
                    const isSelected = selectedFrame === frame.id;
                    return (
                      <button
                        key={frame.id}
                        type="button"
                        onClick={() => {
                          setSelectedFrame(frame.id);
                          toast.success(`Selected: ${frame.name}`);
                        }}
                        className={`text-left p-3 rounded-2xl border transition-all duration-200 relative ${
                          isSelected
                            ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 border-orange-500 shadow-md ring-1 ring-orange-400'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-white">{frame.name}</span>
                          {isSelected && <Check className="w-3.5 h-3.5 text-orange-400" />}
                        </div>
                        <p className="text-[11px] text-gray-400 leading-snug line-clamp-1">{frame.badge}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Add Festive Stickers */}
              <div>
                <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span>2. Add Festive Stickers</span>
                </h3>
                <p className="text-xs text-gray-400 mb-3">Tap stickers to place them on your photo:</p>

                <div className="flex flex-wrap gap-2">
                  {STICKERS.map(sticker => (
                    <button
                      key={sticker.id}
                      type="button"
                      onClick={() => addSticker(sticker)}
                      className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-orange-500/30 hover:border-orange-500/50 border border-white/10 text-xs font-medium text-white transition-all hover:scale-105 flex items-center gap-1.5"
                    >
                      <span className="text-base">{sticker.emoji}</span>
                      <span>{sticker.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Photo Adjustments & Filters */}
              {photo && (
                <div className="space-y-4 pt-2 border-t border-gray-800">
                  <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider flex items-center gap-2">
                    <span>3. Adjust Photo & Color Mood</span>
                  </h3>

                  {/* Filters */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-2">Color Atmosphere</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { id: 'normal', label: 'Original' },
                        { id: 'warm', label: 'Festive' },
                        { id: 'vibrant', label: 'Vibrant' },
                        { id: 'bw', label: 'Vintage' }
                      ].map(f => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setFilterMode(f.id)}
                          className={`py-1.5 text-xs font-bold rounded-lg border transition-all ${
                            filterMode === f.id
                              ? 'bg-orange-500 text-white border-orange-400 shadow-sm'
                              : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Zoom Slider */}
                  <div>
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-300 mb-1">
                      <span className="flex items-center gap-1"><ZoomIn className="w-3.5 h-3.5 text-orange-400" /> Photo Zoom</span>
                      <span>{Math.round(zoom * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.8"
                      max="2.5"
                      step="0.05"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="w-full accent-orange-500"
                    />
                  </div>

                  {/* Custom Banner Text */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">Bottom Caption Text</label>
                    <input
                      type="text"
                      maxLength={40}
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder="e.g. Celebrating Ganesh Fest 2026"
                      className="w-full px-3 py-2 text-xs bg-black/50 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Privacy Notice */}
              <div className="p-3.5 rounded-2xl bg-orange-950/30 border border-orange-500/20 text-xs text-orange-200/80 leading-relaxed flex items-start gap-2.5">
                <Info className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                <p>
                  <strong>100% Client-Side Privacy:</strong> All photo rendering happens locally on your device. Photos are never uploaded to any database without your consent.
                </p>
              </div>

            </div>

          </div>

          {/* Bottom Community Showcase Banner */}
          <div className="mt-12 bg-gradient-to-r from-orange-600 via-amber-600 to-rose-600 rounded-3xl p-6 sm:p-8 text-center text-white shadow-2xl relative overflow-hidden">
            <div className="max-w-2xl mx-auto space-y-3">
              <span className="text-2xl">📸 🥁 ✨</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Tag Us in Your Festival Memories!</h2>
              <p className="text-xs sm:text-sm text-white/90">
                Post your framed photo on Instagram Stories or WhatsApp with <strong className="underline font-bold">#BeatsOfWashington</strong> and tag <strong className="underline font-bold">@beatsofwa</strong> to be featured on our official Community Stories wall!
              </p>
              <div className="pt-2 flex flex-wrap justify-center gap-3">
                <Link to="/stories" className="btn-secondary text-xs sm:text-sm px-5 py-2.5 rounded-xl inline-flex items-center gap-2">
                  <span>View Community Stories</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/events" className="px-5 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-xs sm:text-sm transition-all">
                  Upcoming Festivals
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Saved Photo Modal (100% Reliable Save to Gallery on Mobile + Desktop) */}
      {savedModalImage && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-orange-500/40 rounded-3xl max-w-lg w-full p-5 sm:p-6 shadow-2xl space-y-4 text-white relative animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-xl bg-orange-500/20 text-orange-400">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                </span>
                <div>
                  <h3 className="text-base sm:text-lg font-bold">Your Photo is Ready!</h3>
                  <p className="text-xs text-gray-400">High-Definition Festival Composite</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSavedModalImage(null)}
                className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Rendered Photo Display */}
            <div className="relative rounded-2xl overflow-hidden border-2 border-orange-500/30 bg-black shadow-inner flex items-center justify-center">
              <img
                src={savedModalImage.url}
                alt="Rendered Festival Photo"
                className="w-full max-h-[50vh] object-contain"
              />
            </div>

            {/* Mobile Save to Gallery Hint */}
            <div className="p-3 rounded-xl bg-orange-950/40 border border-orange-500/30 text-xs text-orange-200 flex items-start gap-2.5">
              <Smartphone className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
              <p>
                <strong>Save to Gallery on Mobile:</strong> Tap and hold the photo above and select <strong>&quot;Save to Photos&quot;</strong> or <strong>&quot;Save Image&quot;</strong> to store it in your phone&apos;s camera roll!
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => {
                  if (savedModalImage?.blob) {
                    const file = new File([savedModalImage.blob], savedModalImage.fileName || 'Festival-Photo.jpg', { type: 'image/jpeg' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      navigator.share({
                        files: [file],
                        title: 'Beats of Washington Festival Photo',
                        text: 'Celebrating community with Beats of Washington! 🥁✨ https://beatsofwa.org #BeatsOfWashington'
                      }).catch(() => {});
                    } else {
                      // Trigger download again
                      const link = document.createElement('a');
                      link.download = savedModalImage.fileName || 'Festival-Photo.jpg';
                      link.href = savedModalImage.url;
                      link.click();
                      toast.success('Download re-triggered!');
                    }
                  }
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 transition-all hover:scale-105"
              >
                <Share2 className="w-4 h-4" />
                <span>Save to Photos / Share</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const link = document.createElement('a');
                  link.download = savedModalImage.fileName || 'Festival-Photo.jpg';
                  link.href = savedModalImage.url;
                  link.click();
                  toast.success('Downloaded to device!');
                }}
                className="py-3 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs sm:text-sm border border-white/20 flex items-center justify-center gap-2 transition-all"
              >
                <Download className="w-4 h-4" />
                <span>Download Again</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoBoothPage;
